import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Home, ShoppingBag, CalendarDays, Users, Phone, MapPin, ArrowRight, CheckCircle2, Clock, Send, Smile, HandHelping, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import axios from "axios";
import BeratungsThemen from "@/components/BeratungsThemen";

const API = process.env.REACT_APP_BACKEND_URL;

const LEISTUNGEN = [
  { icon: Home, title: "Unterstützung im Haushalt", desc: "Hilfe bei der Haushaltsführung, Wäsche, Aufräumen und Sauberkeit in Ihrer Wohnung." },
  { icon: ShoppingBag, title: "Einkaufen & Besorgungen", desc: "Wir erledigen Einkäufe für Sie oder begleiten Sie zum Supermarkt." },
  { icon: CalendarDays, title: "Begleitung zu Terminen", desc: "Arzttermine, Behördengänge oder Therapien — wir begleiten Sie sicher hin und zurück." },
  { icon: Smile, title: "Gemeinsame Aktivitäten", desc: "Spaziergänge, Gesellschaftsspiele, Vorlesen oder einfach Gesellschaft und Gespräche." },
  { icon: Users, title: "Entlastung für Angehörige", desc: "Wir übernehmen stundenweise die Betreuung, damit Ihre Angehörigen durchatmen können." },
  { icon: HandHelping, title: "Alltagsstrukturierung", desc: "Unterstützung bei der Tagesplanung, Medikamentenerinnerung und täglichen Routinen." },
];

const REGIONEN = [
  { name: "Grefrath", available: true },
  { name: "Rheinberg", available: true },
];

export default function AlltagsbegleitungPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", nachricht: "", stadt: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Alltagsbegleitung Niederrhein | Grefrath & Rheinberg | Lehnen & Weinacht";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Alltagsbegleitung am Niederrhein: Haushaltshilfe, Einkaufsbegleitung, Betreuung und Entlastung für Angehörige. In Grefrath und Rheinberg (ab 01.01.2027). Über den Entlastungsbetrag (131€/Monat) finanzierbar.");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Bitte füllen Sie mindestens Name und Telefonnummer aus.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/alltagsbegleitung/anfrage`, form);
      setSent(true);
      toast.success("Anfrage erfolgreich gesendet!");
    } catch {
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="alltagsbegleitung-page">
      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-green-50/60 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-50/40 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-full mb-6">
            <Heart className="h-4 w-4 text-green-700" />
            <span className="text-sm font-semibold text-green-800">Anerkanntes Angebot zur Unterstützung im Alltag</span>
          </div>
          <h1 className="font-['Manrope'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            Alltagsbegleitung<br />
            <span className="text-green-700">die ankommt.</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-500 max-w-3xl mx-auto leading-relaxed mb-8">
            Wir unterstützen Pflegebedürftige und ihre Angehörigen im Alltag — persönlich, zuverlässig und finanzierbar über den Entlastungsbetrag Ihrer Pflegekasse.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#anfrage" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-8 py-4 text-lg font-semibold transition-all active:scale-[0.98] shadow-lg hover:shadow-xl" data-testid="alltag-cta-top">
              Alltagsbegleitung anfragen
            </a>
            <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-green-300 text-slate-700 rounded-xl px-8 py-4 text-lg font-semibold transition-all">
              <Phone className="h-5 w-5" /> 0163 791 7044
            </a>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-16 bg-white" id="leistungen-alltag" data-testid="alltag-leistungen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Was wir für Sie tun</h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Unsere Alltagsbegleitung passt sich Ihren Bedürfnissen an — flexibel, menschlich und professionell.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LEISTUNGEN.map((l, i) => {
              const Icon = l.icon;
              return (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`alltag-leistung-${i}`}>
                  <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-green-700" />
                  </div>
                  <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-2">{l.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{l.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Finanzierung */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 rounded-2xl border border-green-200 p-8 sm:p-10">
            <h2 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-4">So wird die Alltagsbegleitung finanziert</h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Unsere Alltagsbegleitung ist als <strong>anerkanntes Angebot zur Unterstützung im Alltag</strong> nach Landesrecht NRW zugelassen. Das bedeutet: Die Kosten können über den <strong>Entlastungsbetrag</strong> Ihrer Pflegekasse abgerechnet werden.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">131 €</p>
                <p className="text-xs text-slate-500 mt-1">Entlastungsbetrag pro Monat</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">Ab PG 1</p>
                <p className="text-xs text-slate-500 mt-1">Bereits ab Pflegegrad 1</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">0 €</p>
                <p className="text-xs text-slate-500 mt-1">Zuzahlung für Sie</p>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Nicht genutzte Beträge können ins Folgequartal übertragen werden. Wir helfen Ihnen bei der Abrechnung mit Ihrer Pflegekasse.
            </p>
          </div>
        </div>
      </section>

      <BeratungsThemen variant="green" ctaHref="#anfrage" />

      {/* Einsatzgebiete */}
      <section className="py-16 bg-white" id="einsatzgebiete" data-testid="alltag-regionen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Wo wir für Sie da sind</h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Unsere Alltagsbegleitung ist in folgenden Regionen am Niederrhein verfügbar.</p>
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
            {[
              { name: "Grefrath", link: "/alltagsbegleitung-grefrath" },
              { name: "Rheinberg", link: "/alltagsbegleitung-rheinberg" },
            ].map((r, i) => (
              r.link ? (
                <Link key={i} to={r.link} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center hover:shadow-md hover:-translate-y-0.5 transition-all group" data-testid={`region-${r.name.toLowerCase()}`}>
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="font-['Manrope'] text-lg font-bold text-slate-800 group-hover:text-green-700">{r.name}</p>
                  <p className="text-xs text-green-600 font-medium mt-1">Mehr erfahren</p>
                </Link>
              ) : (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center" data-testid={`region-${r.name.toLowerCase()}`}>
                  <MapPin className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="font-['Manrope'] text-lg font-bold text-slate-800">{r.name}</p>
                  <p className="text-xs text-green-600 font-medium mt-1">Verfügbar</p>
                </div>
              )
            ))}
          </div>
          <p className="text-center text-sm text-slate-400 mt-6">Sie wohnen in einer anderen Stadt am Niederrhein? Sprechen Sie uns an — wir prüfen, ob wir auch zu Ihnen kommen können.</p>
        </div>
      </section>

      {/* Cross-link zu Pflegeberatung */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-cyan-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="h-14 w-14 bg-cyan-100 rounded-2xl flex items-center justify-center shrink-0">
              <Users className="h-7 w-7 text-cyan-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-1">Brauchen Sie auch Pflegeberatung?</h3>
              <p className="text-sm text-slate-500">Wir beraten Sie kostenfrei zu Pflegegrad, Pflegeleistungen und Beratungsbesuchen nach § 37 Abs. 3 SGB XI — per Video, Telefon oder vor Ort.</p>
            </div>
            <Link to="/" className="shrink-0 inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-5 py-3 font-semibold text-sm transition-all">
              Pflegeberatung <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Anfrage-Formular */}
      <section className="py-16 bg-white" id="anfrage" data-testid="alltag-anfrage">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Alltagsbegleitung anfragen</h2>
            <p className="text-base text-slate-500">Schildern Sie uns kurz Ihre Situation — wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5" data-testid="alltag-anfrage-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Name *</Label>
                  <Input data-testid="alltag-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Ihr Name" className="h-12 rounded-xl" required />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefon *</Label>
                  <Input data-testid="alltag-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    placeholder="Ihre Telefonnummer" className="h-12 rounded-xl" required />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail (optional)</Label>
                  <Input data-testid="alltag-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="ihre@email.de" className="h-12 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Stadt / Wohnort</Label>
                  <Input data-testid="alltag-stadt" value={form.stadt} onChange={e => setForm({...form, stadt: e.target.value})}
                    placeholder="z. B. Grefrath" className="h-12 rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Ihre Nachricht (optional)</Label>
                <textarea data-testid="alltag-nachricht" value={form.nachricht} onChange={e => setForm({...form, nachricht: e.target.value})}
                  placeholder="Erzählen Sie uns kurz, wobei Sie Unterstützung benötigen..."
                  className="w-full h-28 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-400 resize-none" />
              </div>
              <Button type="submit" disabled={sending} data-testid="alltag-submit-btn"
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-xl h-14 text-lg font-semibold shadow-md transition-all">
                {sending ? "Wird gesendet..." : <><Send className="h-5 w-5 mr-2" /> Unverbindlich anfragen</>}
              </Button>
              <p className="text-xs text-slate-400 text-center">Wir melden uns werktags innerhalb von 24 Stunden bei Ihnen.</p>
            </form>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="alltag-anfrage-success">
              <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
              <h3 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-2">Anfrage gesendet!</h3>
              <p className="text-base text-slate-600 mb-6">Vielen Dank für Ihre Anfrage. Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
              <a href="tel:+491637917044" className="inline-flex items-center gap-2 text-green-700 font-semibold hover:underline">
                <Phone className="h-4 w-4" /> Oder rufen Sie uns direkt an: 0163 791 7044
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/alltagsbegleitung" className="hover:text-white transition-colors">Alltagsbegleitung</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
